import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from '@SBGSports/catapult-core';
import { SelectMenuItem } from '@SBGSports/catapult-core/dist/components/select/__commons__/selectCommons';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from '../../analytics/analytics-events';
import { TAGS_PARAM, useQueryParams } from 'shared/use-query-params';
import { GroupedTagItem } from 'reporting/hooks/use-fetch-tag-options';
import { useDebouncedCallback } from 'reporting/hooks/useDebouncedCallback';
import { BaseReportType } from 'reporting/types';
import { TagGroupKey, buildTagFilterKey, parseTagFilterKey, stripTagGroupSuffix } from 'shared/utils/tag-filter-keys';

interface ActivityTagFilterProps {
    tagItems: GroupedTagItem[];
    groupOrder: string[];
    groupKeyMap: Record<string, TagGroupKey>;
    keyToGroupLabel: Partial<Record<TagGroupKey, string>>;
    tagFilters: string[];
    setTagFilters: (tagFilters: string[]) => void;
    reportType?: BaseReportType;
}

export const ActivityTagFilter: React.FC<ActivityTagFilterProps> = ({
    tagItems,
    groupOrder,
    groupKeyMap = {},
    keyToGroupLabel = {},
    tagFilters,
    setTagFilters,
    reportType,
}) => {
    const { __ } = useTranslations();
    const { updateQueryParam } = useQueryParams();

    const [searchValue, setSearchValue] = useState('');
    const [menuItems, setMenuItems] = useState<(GroupedTagItem & SelectMenuItem)[]>([]);

    // Ref tracks the current menuItems so handlers can read synchronously
    const menuItemsRef = useRef<(GroupedTagItem & SelectMenuItem)[]>([]);

    // Names that appear in more than one group (e.g. DayCode "MD+1" AND Activity "MD+1").
    // Disambiguated in URL as "name~groupKey"; unique names are stored plain.
    const duplicateNames = useMemo(() => {
        const counts = new Map<string, number>();
        tagItems.forEach((item) => {
            counts.set(item.value, (counts.get(item.value) ?? 0) + 1);
        });
        return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name));
    }, [tagItems]);

    // Sync items when tag data loads or tagFilters change (e.g. URL-driven).
    // Preserve existing selection by ID so same-name tags in different groups
    // don't cross-select. For new items, parse "name~groupKey" for disambiguated
    // tags or match by plain name (unique tags / backward compat).
    useEffect(() => {
        setMenuItems((prevItems) => {
            const prevSelectedById = new Map(prevItems.map((item) => [item.id, item.isSelected]));

            const exactMatches = new Set<string>();
            const nameMatches = new Set<string>();
            tagFilters.forEach((filter) => {
                const { name, groupKey } = parseTagFilterKey(filter);
                if (groupKey && keyToGroupLabel[groupKey]) {
                    exactMatches.add(`${name}\0${keyToGroupLabel[groupKey]}`);
                } else {
                    nameMatches.add(name);
                }
            });

            return tagItems.map((tag) => ({
                ...tag,
                isSelected: prevSelectedById.has(tag.id)
                    ? prevSelectedById.get(tag.id) ?? false
                    : exactMatches.has(`${tag.value}\0${tag.group}`) || nameMatches.has(tag.value),
            }));
        });
    }, [tagItems, tagFilters, keyToGroupLabel]);

    // Keep ref in sync with state for synchronous reads in handlers
    useEffect(() => {
        menuItemsRef.current = menuItems;
    }, [menuItems]);

    // Build filter keys from selected items: "name~groupKey" for ambiguous names, plain name for unique
    const getFilterKeys = (items: (GroupedTagItem & SelectMenuItem)[]) =>
        items
            .filter((i) => i.isSelected)
            .map((i) => {
                if (duplicateNames.has(i.value)) {
                    return buildTagFilterKey(i.value, groupKeyMap[i.group]);
                }
                return i.value;
            });

    const debouncedSync = useDebouncedCallback(
        (newSelected: string[]) => {
            setTagFilters(newSelected);
            updateQueryParam(TAGS_PARAM, newSelected.join(','));
            try {
                mixpanel.track(EVENTS.sideMenu.activitySearch, { selectedTag: newSelected, reportType });
            } catch {
                // mixpanel throwing error on local dev
            }
        },
        [setTagFilters, updateQueryParam, reportType],
        1000,
    );

    const handleSelectItem = (item: SelectMenuItem) => {
        const updatedItems = menuItemsRef.current.map((o) =>
            o.id === item.id ? { ...o, isSelected: item.isSelected } : o,
        );
        setMenuItems(updatedItems);
        menuItemsRef.current = updatedItems;

        debouncedSync(getFilterKeys(updatedItems));
    };

    const handleSelectAll = (items: SelectMenuItem[]) => {
        const updatedItems = items as (GroupedTagItem & SelectMenuItem)[];
        setMenuItems(updatedItems);
        menuItemsRef.current = updatedItems;

        debouncedSync(getFilterKeys(updatedItems));
    };

    const handleClearAll = () => {
        const cleared = menuItemsRef.current.map((o) => ({ ...o, isSelected: false }));
        setMenuItems(cleared);
        menuItemsRef.current = cleared;
        setSearchValue('');
        debouncedSync([]);
    };

    // Derive display tags from menuItems (updates immediately on select/clear).
    // Falls back to tagFilters while menuItems is still loading.
    const displayTags = useMemo(
        () =>
            menuItems.length > 0
                ? menuItems.filter((i) => i.isSelected).map((i) => i.value)
                : tagFilters.map(stripTagGroupSuffix),
        [menuItems, tagFilters],
    );
    const hasSelection = displayTags.length > 0;

    const placeholder = useMemo(() => {
        if (displayTags.length === 0) {
            return `${__('misc.select')} ${__('misc.tags')}`;
        }
        if (displayTags.length <= 3) {
            return displayTags.join(', ');
        }
        return `${displayTags.length} ${__('misc.selected')}`;
    }, [displayTags, __]);

    return (
        <Select
            id="activity-tag-select"
            searchable="contextual"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            middleMenuItems={menuItems}
            onItemSelect={handleSelectItem}
            multiSelect
            onSelectAll={handleSelectAll}
            groupBy={(item) => (item as GroupedTagItem).group}
            groupOrder={groupOrder}
            size="sm"
            testId="activity-tag-select"
            middleMenuClassName="overflow-y-auto"
            middleMenuStyle={{ maxHeight: '300px' }}
            dropdownStyle={{ zIndex: 12 }}
            inputProps={{
                placeholder,
                clearable: false,
                rightIcon: hasSelection || searchValue ? 'IconRemove' : undefined,
                onRightIconClick: hasSelection || searchValue ? handleClearAll : undefined,
            }}
        />
    );
};
